/**
 * The server's error body is not part of any error type the query client or
 * axios exposes, so it has to be asserted where it is read. Doing that in one
 * place keeps the assertion honest: it is compile-time only, so the value stays
 * nullable and every access is optional. A rejection with no value would
 * otherwise throw inside the error handler and swallow the failure it was
 * reporting.
 */
interface ApiErrorShape {
  response?: {
    data?: {
      /**
       * The shape errorHandler sends for anything reaching next(error).
       * One route (/status) sends a bare string here instead, so this is
       * declared as it is actually sent rather than as it ought to be.
       */
      error?: { message?: string } | string;
      /** The shape a controller sends when it rejects a request directly. */
      message?: string;
    };
  };
  message?: string;
}

/**
 * The most useful message available for a rejected request.
 *
 * The server has two error shapes and both are in use. Anything passed to
 * next(error) is serialised by errorHandler as `{ error: { message } }`, which
 * is the path most failures take; a handful of controllers reject a request
 * directly with a top-level `message`. Checking only the second is why an
 * unlock failure used to surface as "Request failed with status code 500"
 * rather than the reason.
 */
export function errorMessage(error: unknown, fallback: string): string {
  const apiError = error as ApiErrorShape | null | undefined;
  const body = apiError?.response?.data;
  const nested = typeof body?.error === "string" ? body.error : body?.error?.message;
  return nested || body?.message || apiError?.message || fallback;
}
